import { useEffect, useState, forwardRef, useRef, useImperativeHandle } from "react";
import { Box } from "@mui/material";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    ImageRun,
    HeadingLevel,
    ExternalHyperlink,
    AlignmentType,
    Table,
    WidthType,
    TableRow,
    TableCell,
    BorderStyle,
    Header,
    NumberFormat,
    SectionType,
    Footer,
    ShadingType,
    convertInchesToTwip,
} from "docx";

import blankImage from "../../assets/images/user.png";
import { post } from "../../api";

const EventDocxGenerator = forwardRef((props, ref) => {
    const opportunityRef = useRef();
    const speakerImageRef = useRef();
    const sectionsRef = useRef();
    const dataRef = useRef();
    const blankProfileImageRef = useRef();
    const mapSpeakerImage = useRef({});
    const mapImage = useRef({});
    const callbackRef = useRef();
    const orderedListCount = useRef(0);

    useImperativeHandle(ref, () => ({
        generateDocument(callback, opportunity, data, sections) {
            dataRef.current = data;
            callbackRef.current = callback;
            opportunityRef.current = opportunity;
            sectionsRef.current = sections;
            createDocument();
        },
    }));

    const extractImgSrcs = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");
        const imgElements = doc.querySelectorAll("img");
        return Array.from(imgElements).map((img) => img.src);
    };

    const createDocument = async () => {
        let speakerImage = opportunityRef.current.speakerImageURL;

        if (speakerImage) {
            speakerImageRef.current = await displayProfileImage(getThumbnail(speakerImage), "", "", true);
        } else {
            speakerImageRef.current = await displayProfileImage(blankImage, "", "", true);
        }
        let imgSrcs = [];
        for (let section of sectionsRef.current) {
            if (section.sectionType === "Event Contacts") {
                imgSrcs = [...imgSrcs, ...extractImgSrcs(opportunityRef.current.eventContactNotes)];
            } else {
                for (let item of section.subsections) {
                    imgSrcs = [...imgSrcs, ...extractImgSrcs(item.description)];
                }
            }
        }

        if (imgSrcs.length > 0) {
            for (let imgSrc of imgSrcs) {
                if (!mapImage.current[imgSrc]) {
                    mapImage.current[imgSrc] = await loadImageIntoBuffer(imgSrc);
                    mapImage.current[imgSrc] = await displayImageFromNode(mapImage.current[imgSrc]);
                }
            }
        }

        const abstractNumConfig = {
            levels: [
                {
                    level: 0,
                    format: "decimal",
                    text: "%1.",
                    alignment: AlignmentType.START,
                    style: {
                        paragraph: {
                            indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
                        },
                    },
                },
                {
                    level: 1,
                    format: "lowerLetter",
                    text: "%2.",
                    alignment: AlignmentType.START,
                    style: {
                        paragraph: {
                            indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
                        },
                    },
                },
                {
                    level: 2,
                    format: "lowerRoman",
                    text: "%3.",
                    alignment: AlignmentType.START,
                    style: {
                        paragraph: {
                            indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
                        },
                    },
                },
            ],
        };

        let orderedList = [];
        for (let i = 0; i < 200; i++) {
            orderedList.push({
                reference: "ordered-list-" + i,
                ...abstractNumConfig,
            });
        }

        let records = await getEventDetails();

        const doc = new Document({
            numbering: {
                config: orderedList,
            },
            sections: [
                {
                    headers: getHeader(),
                    properties: getDocumentProperty(),
                    footers: getFooter(),
                    children: [...getTitle(), ...records],
                },
            ],
            styles: {
                default: {
                    paragraph: {
                        run: {
                            size: 22,
                            color: "253452",
                            fontFamily: "Calibri",
                        },
                    },
                    listParagraph: {
                        run: {
                            size: 22,
                            color: "253452",
                            fontFamily: "Calibri",
                        },
                    },
                },
                paragraphStyles: [
                    {
                        name: "Normal",
                        run: {
                            size: 22,
                            color: "253452",
                            bold: true,
                            font: "Calibri",
                        },
                    },
                    {
                        id: "ListOverride",
                        name: "ListOverride",
                        basedOn: "Normal",
                        next: "Normal",
                        run: {
                            color: "000000", // 🔴 override the color here
                            font: "Calibri",
                        },
                    },
                ],
            },
        });

        // Used to export the file into a .docx file
        Packer.toBase64String(doc).then((textBlob) => {
            const downloadContainer = document.querySelector(".download-container");
            const downloadUrl =
                "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64," + textBlob;
            const fileName = `WSB x ${opportunityRef.current.clientName} | ${opportunityRef.current.eventName}.docx`;

            let a = document.createElement("a");
            a.href = downloadUrl;
            a.target = "_blank";
            // Use a.download if available, it prevents plugins from opening.
            a.download = fileName;
            // Add a to the doc for click to work.
            if (downloadContainer) {
                downloadContainer.appendChild(a);
            }
            if (a.click) {
                a.click(); // The click method is supported by most browsers.
            }
            // Delete the temporary link.
            downloadContainer.removeChild(a);

            if (callbackRef.current) {
                callbackRef.current();
            }
        });
    };

    const getEventDetails = async () => {
        let records = [];
        let eventBriefing = await displayEventBriefing();
        records = [...records, ...eventBriefing];

        // Sections
        records = [...records, ...displaySections()];

        return records;
    };

    const displayEventBriefing = async () => {
        let records = [];

        let speakerImage = [speakerImageRef.current];
        let speakerInfo = [displayTitle(`${opportunityRef.current?.speakerName}`)];

        let detailsRecords = [];
        if (opportunityRef.current.formattedDate) {
            detailsRecords = [...detailsRecords, ...verticalField("Event Date", opportunityRef.current.formattedDate)];
        }

        dataRef.current = dataRef.current ? dataRef.current : {};

        if (dataRef.current.hotel === dataRef.current.venue) {
            detailsRecords = [...detailsRecords, ...verticalField("Hotel/Venue", dataRef.current.hotel)];
        } else {
            if (dataRef.current.venue) {
                detailsRecords = [...detailsRecords, ...verticalField("Venue", dataRef.current.venue)];
            }
            if (dataRef.current.hotel) {
                detailsRecords = [...detailsRecords, ...verticalField("Hotel", dataRef.current.hotel)];
            }
        }

        if (opportunityRef.current.emNotes) {
            detailsRecords = [
                ...detailsRecords,
                ...verticalField("Notes", opportunityRef.current.emNotes.replaceAll("\n", "<br>"), "richtext"),
            ];
        }

        let table = new Table({
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            children: [...speakerImage, ...speakerInfo],
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                            },
                        }),
                        new TableCell({
                            children: [],
                            width: {
                                size: 10,
                                type: WidthType.PERCENTAGE,
                            },
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                            },
                        }),
                        new TableCell({
                            children: [...detailsRecords],
                            width: {
                                size: 70,
                                type: WidthType.PERCENTAGE,
                            },
                            borders: {
                                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                            },
                        }),
                    ],
                }),
            ],
        });

        records.push(table);
        records.push(displayDividerEndOfSpeaker());

        return records;
    };

    const getTitle = () => {
        return [
            new Paragraph({
                children: [
                    new TextRun({
                        children: [`WSB x ${opportunityRef.current.clientName} | ${opportunityRef.current.eventName}`],
                        font: "Calibri",
                        bold: false,
                        color: "1A2847",
                        size: 36,
                    }),
                ],
                heading: HeadingLevel.HEADING_3,
                alignment: AlignmentType.LEFT,
                spacing: {
                    after: 300,
                    before: 100,
                },
            }),
            ...displayRichtextArea(
                opportunityRef.current.proposalIntroduction,
                {
                    heading: HeadingLevel.HEADING_3,
                    alignment: AlignmentType.LEFT,
                    thematicBreak: true,
                    spacing: {
                        after: 200,
                    },
                },
                { color: "0084D4", size: 26 }
            ),
        ];
    };

    const displaySections = () => {
        if (sectionsRef.current?.length === 0) {
            return [];
        }

        let records = [];
        for (let section of sectionsRef.current) {
            if (section.sectionType !== "Files") {
                const name = section.name === "About Client" ? "Client Details" : section.name;
                records = [...records, displayHeading(name, false)];
            }

            if (section.sectionTabValue === "Itinerary") {
                records = [...records, ...displayItinerary(section)];
            } else if (section.sectionType === "Event Contacts") {
                records = [...records, ...displayEventContacts(section)];
            } else if (section.sectionType === "Files") {
                //records = [...records, ...displayEventContacts(section)];
            } else {
                records = [...records, ...displayDocumentSections(section)];
            }
        }

        return records;
    };

    const displayDocumentSections = (section) => {
        let records = [];
        if (section?.subsections && section?.subsections.length > 0) {
            for (let item of section.subsections) {
                if (
                    item.description?.trim() &&
                    item.description?.replaceAll(/<[^>]*>/g, "")?.trim() &&
                    item.description?.trim() !== "<p></p>"
                ) {
                    records = [
                        ...records,
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: item.name,
                                    size: 26,
                                    bold: true,
                                    color: "344054", // A shade of blue
                                }),
                            ],
                            spacing: {
                                after: 30,
                            },
                        }),
                    ];
                    if (item.description) {
                        records = [
                            ...records,
                            ...displayRichtextArea(item.description.replaceAll("\n", "<p>")),
                            new Paragraph({
                                children: [""],
                                spacing: {
                                    after: 30,
                                },
                            }),
                        ];
                    }
                }
            }
        }

        return records;
    };

    const displayEventContacts = (section) => {
        let eventContactData = [];
        if (opportunityRef.current?.primaryClientEMId) {
            eventContactData.push({ ...opportunityRef.current.primaryClientEM, title: "Primary Event Manager" });
        }
        if (opportunityRef.current?.secondaryClientEMId) {
            eventContactData.push({ ...opportunityRef.current.secondaryClientEM, title: "Secondary Event Manager" });
        }
        if (opportunityRef.current.dayOfEventEMId) {
            eventContactData.push({ ...opportunityRef.current.dayOfEvent, title: "Day of Event Manager" });
        }

        let bookingRecords = [];
        if (opportunityRef.current.bookingAgentId) {
            bookingRecords.push({ ...opportunityRef.current.bookingAgent, title: "Booking Agent" });
        }

        let leadRecords = [];
        if (opportunityRef.current.associateLeadId) {
            leadRecords.push({
                ...opportunityRef.current.associateLead,
                title: "Associate Lead",
                displayOnlyEmail: true,
            });
        }

        let records = [];

        if (eventContactData.length > 0) {
            records = [
                ...records,
                ...displayContact(
                    eventContactData.map((item) => ({ first: item.title, second: item.Name, third: item.Email, fourth: item.Phone }))
                ),
            ];
        }

        if (bookingRecords.length > 0) {
            records = [
                ...records,
                ...displayContact(
                    bookingRecords.map((item) => ({ first: item.title, second: item.Name, third: item.Email, fourth: item.Phone  }))
                ),
            ];
        }

        if (leadRecords.length > 0) {
            records = [
                ...records,
                ...displayContact(
                    leadRecords.map((item) => ({ first: item.title, second: item.Name, third: item.Email }))
                ),
            ];
        }
        debugger;
        if (opportunityRef.current?.eventContactNotes) {
            records.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: "Contact Notes",
                            size: 30,
                            bold: true,
                            color: "344054", // A shade of blue
                        }),
                    ],
                    spacing: {
                        after: 30,
                    },
                })
            );
            records = [...records, ...displayRichtextArea(opportunityRef.current.eventContactNotes)];
        }

        return [...records];
    };

    const displayContact = (records) => {
        let cells = [];
        for (let item of records) {
            cells.push(
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: item.first,
                                    size: 20,
                                    bold: true,
                                    color: "DD6B20",
                                }),
                            ],
                            spacing: {
                                after: 30,
                            },
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: item.second,
                                    size: 30,
                                    bold: true,
                                    color: "101828",
                                }),
                            ],
                            spacing: {
                                after: 30,
                            },
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: item.third,
                                    size: 20,
                                    bold: false,
                                    color: "475467",
                                }),
                            ],
                            spacing: {
                                after: 30,
                            },
                        }),
                        ...(item.fourth
                            ? [
                                  new Paragraph({
                                      children: [
                                          new TextRun({
                                              text: item.fourth,
                                              size: 20,
                                              bold: true,
                                              color: "DD6B20",
                                          }),
                                      ],
                                      spacing: {
                                          after: 30,
                                      },
                                  }),
                              ]
                            : []),
                    ],
                    margins: {
                        top: 100,
                        bottom: 100,
                        left: 150,
                        right: 150,
                    },
                    borders: {
                        top: { style: BorderStyle.SINGLE, size: 0, color: "c0d5fb" },
                        bottom: { style: BorderStyle.SINGLE, size: 0, color: "c0d5fb" },
                        left: { style: BorderStyle.SINGLE, size: 0, color: "c0d5fb" },
                        right: { style: BorderStyle.SINGLE, size: 0, color: "c0d5fb" },
                    },
                })
            );
        }

        const table = new Table({
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            rows: [
                new TableRow({
                    children: [...cells],
                }),
            ],
        });

        return [
            table,
            new Paragraph({
                children: [""],
                spacing: {
                    after: 30,
                },
            }),
        ];
    };

    const displayItinerary = (section) => {
        let records = [];
        let data = section.data ? JSON.parse(section.data)?.filter((item) => item.recordTypeName !== "Timeline") : [];
        data = getGroupedActivities(data);
        for (let item of data) {
            records = [...records, ...displayItinerarySectionHeader(item.departureStart)];
            for (let activity of item.items) {
                records = [...records, ...displayItinerarySectionCard(activity)];
            }
        }

        return records;
    };

    const displayItinerarySectionCard = (activity) => {
        // Second row: Drive time + Additional Notes
        let content = dataRef.current?.itinerary[activity.id] || [];
        let leftContact = [];
        let rightContact = [];
        Object.keys(content).forEach((key) => {
            if (content[key].side === "left") {
                leftContact.push({ key: key, ...content[key] });
            } else {
                rightContact.push({ key: key, ...content[key] });
            }
        });

        let leftData = [];
        let rightData = [];
        leftContact = leftContact.sort((a, b) => a.order - b.order);
        for (let item of leftContact) {
            leftData = [...leftData, ...horizontalField(item.key, item.value)];
        }
        rightContact = rightContact.sort((a, b) => a.order - b.order);
        for (let item of rightContact) {
            rightData = [...rightData, ...verticalField(item.key, item.value, item.type)];
        }

        let subjectData = [];
        if (
            activity.recordTypeName === "Rail" ||
            activity.recordTypeName === "Flight" ||
            activity.recordTypeName === "Private Flight"
        ) {
            subjectData = [new TextRun({ text: activity.subject, bold: true })];
        } else {
            subjectData = [
                new TextRun({
                    text: `${activity.departureStartTimeString} ${activity.departureTimeZoneCode} | `,
                    color: "DD6B20",
                    bold: true,
                }),
                new TextRun({ text: activity.subject, bold: true }),
            ];
        }

        const detailRow = new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: activity.recordTypeName, size: 28, bold: true })],
                            spacing: {
                                after: 30,
                            },
                        }),
                        new Paragraph({
                            children: subjectData,
                        }),
                        new Paragraph({
                            children: [""],
                            spacing: {
                                after: 30,
                            },
                        }),
                        ...leftData,
                    ],
                    width: { size: rightData.length ? 70 : 100, type: WidthType.PERCENTAGE },
                    margins: {
                        top: 100,
                        bottom: 100,
                        left: 150,
                        right: 150,
                    },
                    shading: {
                        type: ShadingType.CLEAR,
                        color: "auto",
                        fill: "FAFAF9", // light blue background
                    },
                }),
                ...(rightData?.length
                    ? [
                          new TableCell({
                              children: [...rightData],
                              width: { size: 30, type: WidthType.PERCENTAGE },
                              margins: {
                                  top: 100,
                                  bottom: 100,
                                  left: 150,
                                  right: 150,
                              },
                              shading: {
                                  type: ShadingType.CLEAR,
                                  color: "auto",
                                  fill: "FAFAF9", // light blue background
                              },
                          }),
                      ]
                    : []),
            ],
        });
        const fullTable = new Table({
            rows: [detailRow],
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                insideVertical: rightData.length ? { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" } : { style: BorderStyle.NONE },
            },
        });

        return [
            fullTable,
            new Paragraph({
                children: [""],
                spacing: {
                    after: 30,
                },
            }),
        ];
    };

    const displayItinerarySectionHeader = (item) => {
        const dateParagraph = new Paragraph({
            children: [
                new TextRun({
                    text: item,
                    size: 24,
                    bold: true,
                    color: "1E4DB7", // A shade of blue
                }),
            ],
            spacing: {
                before: 100,
                after: 100,
            },
            indent: {
                left: 150, // left padding (~15pt)
                right: 150,
            },
        });

        // Wrap it in a styled table cell to simulate a rounded box background
        const table = new Table({
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            children: [dateParagraph],
                            shading: {
                                type: ShadingType.CLEAR,
                                color: "auto",
                                fill: "F7F9FF", // light blue background
                            },
                            borders: {
                                top: { style: BorderStyle.SINGLE, size: 1, color: "c0d5fb" },
                                bottom: { style: BorderStyle.SINGLE, size: 1, color: "c0d5fb" },
                                left: { style: BorderStyle.SINGLE, size: 1, color: "c0d5fb" },
                                right: { style: BorderStyle.SINGLE, size: 1, color: "c0d5fb" },
                            },
                        }),
                    ],
                }),
            ],
        });

        return [
            table,
            new Paragraph({
                children: [""],
                spacing: {
                    after: 30,
                },
            }),
        ];
    };

    const getGroupedActivities = (activities) => {
        let displayActivity = [...activities];

        // adding dummy data
        displayActivity = displayActivity?.map((cItem, index) => {
            return cItem;
        });

        const groupedDisplayActivity = Object.values(
            displayActivity?.reduce((acc, obj) => {
                let date = obj.departureStartDateString;
                if (!acc[date]) {
                    acc[date] = { departureStart: obj.departureStartDateString, items: [] };
                }
                acc[date].items.push(obj);
                return acc;
            }, {})
        );

        return groupedDisplayActivity;
    };

    const horizontalField = (text, value, icon) => {
        let typeValue = "text";

        if (value.indexOf("\n") > -1 && text !== "Departure" && text !== "Arrival") {
            value = value.replaceAll("\n", "<p>");
            typeValue = "richtext";
        }

        if (value.indexOf("<br>") > -1 && text !== "Departure" && text !== "Arrival") {
            value = value.replaceAll("<br>", "<p>");
            typeValue = "richtext";
        }

        if (value.indexOf("<p>") > -1) {
            typeValue = "richtext";
        }

        let valueData = [];
        if (typeValue === "richtext") {
            valueData = displayRichtextArea(
                value,
                {},
                {
                    size: 20,
                    bold: false,
                    color: "101828",
                }
            );
        } else {
            valueData = [
                new Paragraph({
                    children: [new TextRun({ text: value, bold: false, size: 20 })],
                }),
            ];
        }

        const detailRow = new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: text, bold: true, size: 18 })],
                        }),
                    ],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                    margins: {
                        top: 100,
                        bottom: 100,
                        left: 150,
                        right: 150,
                    },
                    shading: {
                        type: ShadingType.CLEAR,
                        color: "auto",
                        fill: "FFFFFF", // light blue background
                    },
                }),
                new TableCell({
                    children: valueData,
                    width: { size: 70, type: WidthType.PERCENTAGE },
                    margins: {
                        top: 100,
                        bottom: 100,
                        left: 150,
                        right: 150,
                    },
                    shading: {
                        type: ShadingType.CLEAR,
                        color: "auto",
                        fill: "FFFFFF", // light blue background
                    },
                }),
            ],
        });
        let tableData = new Table({
            rows: [detailRow],
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E9E9E9" },
            },
        });

        return [tableData, new Paragraph({ children: [""], spacing: { after: 0 } })];
    };

    const verticalField = (text, value, typeValue) => {
        if (value.indexOf("\n") > -1) {
            value = value.replaceAll("\n", "<p>");
            typeValue = "richtext";
        }
        if (value.indexOf("<br>") > -1) {
            value = value.replaceAll("<br>", "<p>");
            typeValue = "richtext";
        }
        if (value.indexOf("<p>") > -1) {
            typeValue = "richtext";
        }

        if (typeValue === "button") {
            return [
                new Paragraph({
                    children: [
                        new ExternalHyperlink({
                            children: [
                                new TextRun({
                                    children: [`${text}`],
                                    allCaps: true,
                                    font: "Calibri",
                                    size: 20,
                                    color: "253452",
                                    bold: true,
                                }),
                            ],
                            link: value?.startsWith("http") ? value : `https://${value}`,
                        }),
                    ],
                    // heading: docx.HeadingLevel.TITLE,
                    alignment: AlignmentType.LEFT,
                    thematicBreak: false,
                    spacing: {
                        before: 30,
                        after: 30,
                    },
                }),
            ];
        }

        const eventDateLabel = new Paragraph({
            children: [
                new TextRun({
                    text: text,
                    bold: true,
                    color: "2D3E50", // Matching the navy/dark blue color
                    size: 18,
                }),
            ],
            spacing: {
                after: 2,
            },
        });
        if (typeValue === "richtext") {
            const eventDateValue = displayRichtextArea(
                value,
                {},
                {
                    size: 20,
                    bold: false,
                    color: "101828",
                }
            );
            return [eventDateLabel, ...eventDateValue];
        }
        if (typeValue === "link") {
            const eventDateValue = new Paragraph({
                children: [
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: value,
                                size: 20,
                                bold: false,
                                color: "101828",
                            }),
                        ],
                        link: value?.startsWith("http") ? value : `https://${value}`,
                    }),
                ],
                spacing: {
                    before: 50,
                    after: 200,
                },
            });

            return [eventDateLabel, eventDateValue];
        } else {
            const eventDateValue = new Paragraph({
                children: [
                    new TextRun({
                        text: value,
                        size: 20,
                        bold: false,
                        color: "101828",
                    }),
                ],
                spacing: {
                    before: 50,
                    after: 200,
                },
            });

            return [eventDateLabel, eventDateValue];
        }
    };

    const getFooter = () => {
        return {
            default: new Footer({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        heading: HeadingLevel.HEADING_3,
                        children: [
                            new TextRun({
                                children: [`833.972.8255  |  WWW.WSB.COM`],
                                size: 12,
                                color: "158CD7",
                            }),
                        ],
                    }),
                ],
            }),
        };
    };

    const getDocumentProperty = () => {
        return {
            page: {
                pageNumbers: {
                    start: 1,
                    formatType: NumberFormat.DECIMAL,
                },
            },
            properties: {
                type: SectionType.CONTINUOUS,
            },
        };
    };

    const getHeader = () => {
        return {
            default: new Header({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        thematicBreak: true,
                        heading: HeadingLevel.HEADING_3,
                        children: [
                            new TextRun({
                                children: ["wsb"],
                                font: "Tahoma",
                                bold: true,
                                color: "253452",
                                size: 100,
                            }),
                        ],
                        spacing: {
                            after: 100,
                        },
                    }),
                ],
            }),
        };
    };

    const displayDividerEndOfSpeaker = () => {
        return new Paragraph({
            text: "",
            spacing: {
                after: 150,
            },
            thematicBreak: true,
        });
    };

    const displayParagraph = (text, bold = false) => {
        return new Paragraph({
            children: [
                new TextRun({
                    children: [`${text}`],
                    font: "Calibri",
                    color: "000000",
                    bold: bold,
                    size: 24,
                }),
            ],
            thematicBreak: false,
            spacing: {
                after: 30,
            },
            indent: {
                left: 720,
            },
        });
    };

    const displayParagraphURLWithBullet = (text, url) => {
        return new Paragraph({
            children: [
                new ExternalHyperlink({
                    children: [
                        new TextRun({
                            children: [`${text}`],
                            font: "Calibri",
                            color: "253452",
                            size: 22,
                            style: "Hyperlink",
                        }),
                    ],
                    link: url,
                }),
            ],
            heading: HeadingLevel.HEADING_3,
            bullet: {
                level: 0,
            },
            thematicBreak: false,
        });
    };

    const displayHeading = (text, pageBreak) => {
        console.log("Displaying speaker detail heading");
        return new Paragraph({
            children: [
                new TextRun({
                    children: [`${text}`],
                    font: "Calibri",
                    color: "1A2847",
                    size: 38,
                    bold: true,
                }),
            ],
            heading: HeadingLevel.HEADING_3,
            spacing: {
                before: 400,
                after: 100,
            },
            thematicBreak: false,
            pageBreakBefore: pageBreak,
        });
    };

    const displayTextArea = (value, setting, textRunSetting, color) => {
        if (value) {
            setting = setting ? setting : {};
            textRunSetting = textRunSetting ? textRunSetting : {};
            let records = [];
            let allParas = value.split(/\r?\n/);
            for (let para of allParas) {
                let textRun = new TextRun({
                    children: [para],
                    font: "Calibri",
                    color: color ? color : "000000",
                    size: 24,
                    ...textRunSetting,
                });

                let record = new Paragraph({
                    children: [textRun],
                    ...setting,
                    thematicBreak: false,
                    spacing: {
                        after: 30,
                    },
                });
                records.push(record);
            }

            return records;
        }
        return [];
    };

    const displaySubHeading = (text, size = 32) => {
        return new Paragraph({
            children: [
                new TextRun({
                    children: [`${text}`],
                    font: "Calibri",
                    allCaps: true,
                    bold: true,
                    color: "808080",
                    size: size,
                }),
            ],
            heading: HeadingLevel.HEADING_3,
            thematicBreak: false,
            spacing: {
                after: 100,
            },
        });
    };

    const displayRichtextArea = (value, setting, textRunSetting) => {
        let records = [];
        if (value) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(value, "text/html");

            // console.log('The original doc element child nodes', doc.body.childNodes);
            if (doc.body.childNodes.length > 0) {
                for (let node of doc.body.childNodes) {
                    // console.log('-- The node: ', node.nodeName, node.textContent);

                    if (node.nodeName === "DIV") {
                        const divContent = displayRichtextArea(node.innerHTML, setting, textRunSetting);
                        records.push(...divContent);
                    } else if (node.nodeName === "P") {
                        // let textRun = new TextRun({
                        //     children: [node.textContent],
                        //     font: "Calibri",
                        //     ...textRunSetting,
                        //     bold: false,
                        // });

                        // let record = new Paragraph({
                        //     children: [textRun],
                        //     ...setting,
                        //     spacing: {
                        //         after: 30,
                        //     },
                        // });
                        // if (node.textContent) {
                        //     records.push(record);
                        // }

                        let data = parseNode(node, textRunSetting);
                        records = [...records, ...data];
                    } else if (node.nodeName === "UL" || node.nodeName === "OL") {
                        let lis = node.children;
                        if (node.nodeName === "OL") {
                            orderedListCount.current++;
                        }
                        if (lis.length > 0) {
                            for (let li of lis) {
                                let liText = li.outerHTML;
                                liText = liText.replaceAll("<p>", "").replaceAll("</p>", "");
                                const parser = new DOMParser();
                                const doc = parser.parseFromString(liText, "text/html");
                                let parsedData = parseNode(doc.body.childNodes[0], textRunSetting, 0);
                                let para = parsedData.filter((item) => item instanceof Paragraph);
                                let textRuns = parsedData.filter((item) => item instanceof TextRun);
                                const paragraphOptions = {
                                    children: textRuns,
                                    ...setting,
                                    indent: {
                                        left: 350,
                                    },
                                };

                                // Add numbering for OL, bullets for UL
                                if (node.nodeName === "OL") {
                                    paragraphOptions.numbering = {
                                        reference: "ordered-list-" + orderedListCount.current, // You need to define this numbering reference
                                        level: 0,
                                    };
                                } else {
                                    paragraphOptions.bullet = {
                                        level: 0,
                                    };
                                }

                                records.push(new Paragraph(paragraphOptions));
                                if (para.length > 0) {
                                    for (let p of para) {
                                        records.push(p);
                                    }
                                }
                            }
                        }
                    } else if (node.nodeName === "#text") {
                        // console.log('Adding a text element with value: ', node.textContent)
                        let textRun = new TextRun({
                            children: [node.textContent],
                            font: "Calibri",
                            ...textRunSetting,
                            bold: false,
                        });

                        let record = new Paragraph({
                            children: [textRun],
                            ...setting,
                            spacing: {
                                after: 30,
                            },
                        });
                        records.push(record);
                    }
                }
            }
            return records;
        }
        return [];
    };

    const rgbToHex = (rgb) => {
        const result = rgb.match(/\d+/g);
        if (!result || result.length < 3) return null;

        return (
            "#" +
            result
                .slice(0, 3) // Only take R, G, B
                .map((num) => {
                    const hex = parseInt(num).toString(16);
                    return hex.length === 1 ? "0" + hex : hex;
                })
                .join("")
        );
    };

    const parseNode = (node, textRunSetting, level = 0, inheritedStyles = {}, initial = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.trim();
            return text
                ? [
                      new TextRun({
                          children: [text],
                          bold: false,
                          font: "Calibri",
                          ...textRunSetting,
                          ...inheritedStyles,
                      }),
                  ]
                : [];
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return [];

        if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName.toLowerCase();
            const styleAttr = node.getAttribute("style") || "";

            // Map inline styles
            styleAttr.split(";").forEach((rule) => {
                const [key, value] = rule.split(":").map((str) => str?.trim()?.toLowerCase());
                if (key === "color") {
                    inheritedStyles.color = value.startsWith("rgb")
                        ? rgbToHex(value)?.replace("#", "")
                        : value?.replace("#", "");
                }
                if (key === "text-decoration" && value?.includes("underline")) {
                    inheritedStyles.underline = true;
                }
            });
        }

        const el = node;
        const tag = el.tagName.toLowerCase();

        let children = [];
        if (!(tag === "ul" || tag === "ol")) {
            children = Array.from(el.childNodes).flatMap((child) => {
                if (initial === 0) {
                    let color = inheritedStyles.color;
                    inheritedStyles = {
                        bold: false,
                        italics: false,
                        underline: false,
                        strike: false,
                        ...textRunSetting,
                    };
                    if (color) {
                        //inheritedStyles.color = color;
                    }
                }
                return parseNode(child, textRunSetting, level, inheritedStyles, 1);
            });
        }

        switch (tag) {
            case "br":
                return [new TextRun({ break: 1 })];

            case "b":
            case "strong":
                // return [
                //     new TextRun({
                //         children: [node.textContent?.endsWith(":") ? node.textContent + " " : node.textContent],
                //         bold: true,
                //         font: "Calibri",
                //         ...textRunSetting,
                //     }),
                // ];

                return children.map((child) => {
                    inheritedStyles.bold = true;
                    return child instanceof TextRun
                        ? new TextRun({ ...child, bold: true, text: node.textContent, ...inheritedStyles })
                        : child;
                });

            case "i":
            case "em":
                return children.map((child) => {
                    inheritedStyles.italics = true;
                    return child instanceof TextRun
                        ? new TextRun({ ...child, italics: true, text: node.textContent, ...inheritedStyles })
                        : child;
                });
            case "strike": {
                return children.map((child) => {
                    inheritedStyles.strike = true;
                    return child instanceof TextRun
                        ? new TextRun({ ...child, italics: true, text: node.textContent, ...inheritedStyles })
                        : child;
                });
            }

            case "u":
                return children.map((child) => {
                    console.log("The child: ", child);
                    inheritedStyles.underline = {};
                    return child instanceof TextRun
                        ? new TextRun({ ...child, underline: {}, text: node.textContent, ...inheritedStyles })
                        : child;
                });

            case "a": {
                const href = el.getAttribute("href");
                if (href) {
                    const linkText = children.map((c) => c.text || "").join("");
                    return [
                        new ExternalHyperlink({
                            link: href,
                            children: [new TextRun({ text: linkText, style: "Hyperlink" })],
                        }),
                    ];
                }
                return children;
            }

            case "p": {
                let newData = children;
                if (newData.length > 1) {
                    newData = [];
                    for (let item of children) {
                        newData.push(item);
                        if (item instanceof TextRun) {
                            newData.push(new TextRun({ text: " " }));
                        }
                    }
                }
                return [new Paragraph({ children: [new TextRun({ text: "" }), ...newData] })];
            }

            case "h1":
            case "h2":
            case "h3":
            case "h4":
            case "h5":
            case "h6": {
                const headingMap = {
                    h1: HeadingLevel.HEADING_1,
                    h2: HeadingLevel.HEADING_2,
                    h3: HeadingLevel.HEADING_3,
                    h4: HeadingLevel.HEADING_4,
                    h5: HeadingLevel.HEADING_5,
                    h6: HeadingLevel.HEADING_6,
                };
                return [new Paragraph({ heading: headingMap[tag], children })];
            }
            case "img": {
                debugger;
                return mapImage.current[node.src] ? [mapImage.current[node.src]] : []; // Assuming mapImage.current is a map of image URLs to ImageRun objects;
            }

            case "ul":
            case "ol": {
                const isOrdered = tag === "ol";

                return Array.from(el.children).flatMap((li) => {
                    if (li.tagName.toLowerCase() !== "li") return [];

                    const liChildren = [];
                    for (const child of li.childNodes) {
                        // If nested list, increase level
                        if (
                            child.tagName &&
                            (child.tagName.toLowerCase() === "ul" || child.tagName.toLowerCase() === "ol")
                        ) {
                            liChildren.push(...parseNode(child, textRunSetting, level + 1));
                        } else {
                            liChildren.push(...parseNode(child, textRunSetting, level + 1));
                        }
                    }

                    return [
                        new Paragraph({
                            children: liChildren,
                            ...(isOrdered
                                ? {
                                      numbering: {
                                          reference: "ordered-list-" + orderedListCount.current,
                                          level: level + 1,
                                      },
                                  }
                                : { bullet: { level: level + 1 } }),
                            indent: { left: 350 * (level + 1.4) },
                        }),
                    ];
                });
            }

            default:
                return children;
        }
    };

    const displayTitle = (text) => {
        return new Paragraph({
            children: [
                new TextRun({
                    children: [`${text}`],
                    allCaps: true,
                    font: "Calibri",
                    size: 32,
                    color: "253452",
                    bold: true,
                }),
            ],
            // heading: docx.HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            thematicBreak: false,
            spacing: {
                after: 50,
            },
        });
    };

    const displayTitleLink = (text, url) => {
        return new Paragraph({
            children: [
                new ExternalHyperlink({
                    children: [
                        new TextRun({
                            children: [`${text}`],
                            allCaps: true,
                            font: "Calibri",
                            size: 36,
                            color: "253452",
                            bold: true,
                        }),
                    ],
                    link: `https://${url}`,
                }),
            ],
            // heading: docx.HeadingLevel.TITLE,
            alignment: AlignmentType.LEFT,
            thematicBreak: false,
            spacing: {
                after: 50,
            },
        });
    };

    const displayGroupDescription = (text, bold = false) => {
        return new Paragraph({
            children: [
                new TextRun({
                    children: [`${text}`],
                    font: "Calibri",
                    color: "1f4d78",
                    bold: bold,
                    size: 24,
                }),
            ],
            thematicBreak: false,
            spacing: {
                after: 300,
            },
        });
    };

    const convertHtmlToText = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        // console.log(doc.body.textContent);
        return doc.body.textContent || "";
    };
    const getThumbnail = (url) => {
        // Define the prefix to insert
        const prefix = "H-170";

        // Find the position to insert the prefix
        const urlParts = url.split("/");
        const insertIndex = 3; // After the third slash

        // Insert the prefix into the URL
        urlParts.splice(insertIndex, 0, prefix);

        // Join the parts back together
        const newUrl = urlParts.join("/");

        return newUrl;
    };

    const displayImageFromNode = (url, setting) => {
        return new Promise((resolve, reject) => {
            const imageData = new Image();
            imageData.src = url;
            imageData.onload = async () => {
                const imageWidth = imageData.width;
                const imageHeight = imageData.height;

                const maxWidth = 600; //width ? width : 600; // Maximum width in pixels
                const maxHeight = 800; //height ? height : 800; // Maximum height in pixels
                let newWidth = imageWidth;
                let newHeight = imageHeight;

                if (imageWidth > maxWidth) {
                    newWidth = maxWidth;
                    newHeight = (maxWidth / imageWidth) * imageHeight;
                }
                if (newHeight > maxHeight) {
                    newHeight = maxHeight;
                    newWidth = (maxHeight / imageHeight) * imageWidth;
                }
                const imageBuffer = base64ToBuffer(url);
                const image = new ImageRun({
                    data: imageBuffer,
                    transformation: {
                        width: newWidth,
                        height: newHeight,
                    },
                });

                let para = new Paragraph({
                    children: [image],
                    ...setting,
                });
                resolve(para);
            };
            imageData.onerror = (error) => {
                console.error("Error loading image:", error);
                reject(error);
            };
        });
    };

    const displayProfileImage = async (url, width, height, isLocal) => {
        let imageBuffer = null;
        if (isLocal) {
            imageBuffer = await loadImageIntoBufferLocal(url);
        } else {
            imageBuffer = await loadImageIntoBuffer(url);
        }

        return new Paragraph({
            children: [
                new ImageRun({
                    data: imageBuffer,
                    transformation: {
                        width: width ? width : 140,
                        height: height ? height : 170,
                    },
                }),
            ],
            heading: HeadingLevel.HEADING_3,
            spacing: {
                after: 30,
            },
            thematicBreak: false,
        });
    };

    const loadImageIntoBuffer = (url) => {
        return new Promise(async (resolve, reject) => {
            let result = await post("custom/getURLBlob", { url: url });
            if (result.isSuccess) {
                //let bufferData = base64ToBuffer(result.data);
                //resolve(bufferData);
                resolve(result.data);
            }
        });
    };

    const base64ToBuffer = (base64) => {
        // Remove data URL prefix if present
        const base64Data = base64.replace(/^data:image\/\w+;base64,/, "");

        // Decode base64 string to binary data
        const binaryData = atob(base64Data);

        // Create buffer from binary data
        const buffer = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
            buffer[i] = binaryData.charCodeAt(i);
        }

        return buffer;
    };

    const loadImageIntoBufferLocal = (url) => {
        return new Promise(async (resolve, reject) => {
            const headers = {
                origin: "*", // Replace with your origin URL
            };
            await fetch(url, {
                headers: headers,
            })
                .then((response) => response.blob())
                .then((blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        var data = reader.result;
                        const buffer = new Uint8Array(data);
                        resolve(buffer);
                    };
                    reader.readAsArrayBuffer(blob);
                })
                .catch((error) => {
                    console.log("Error loading image:", error);
                    reject(error);
                });
        });
    };

    return (
        <Box>
            <div className="download-container"></div>
        </Box>
    );
});

export { EventDocxGenerator };
